import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import Portal from '@ui/portal/portal';
import { setAnchorPosition } from './set-anchor-position';
import { setTailPosition } from './set-tail-position';
import { usePositionOnScroll } from './use-position-on-scroll';
import { ITooltipPortalProps, TooltipPortalContentDirection, TooltipPortalPosition } from './tooltip-portal-types';
import OptionalComponent from '@ui/optional-component/optional-component';
import useClickAwayListener from '@/utils/use-click-away-listener';

import './tooltip-portal.css';

/* 
  TIPS:
    - if you want to apply hover styles on the parent component your tooltip lives in
      you can use css `&:has(.tooltip-portal-wrapper.active) { ... }` to apply styles.
      There is a good example of that in `theme-pack-overview-card-slim.css`
*/

const TooltipPortal = ({
  children,
  tooltipContent,
  position = TooltipPortalPosition.TOP,
  contentDirection = TooltipPortalContentDirection.CENTER,
  styles = {},
  portalWrapperStyles = {},
  horizontalOffset = 1, // using 1px to account for border radius
  verticalOffset = 0,
  childrenWrapperClass = '',
  contentWrapperClass = '',
  tooltipWrapperClass = '',
  portalHover,
  stopPropagation = true,
  disabled: propsDisabled,
  delayStart = 0,
  tailSize = 7,
  keepOpenOnContentHover = true,
  animationType = 'fade',
  open,
  slideDisplacement = '7px',
  applyActiveSvgFillColor = false,
  showOnFocus = false,
  showOnHover = true,
  alwaysCloseOnMouseOut,
  closeOnEscape,
  onTooltipClick,
  onClickAway,
  onEscape,
  onTab,
  tailHorizontalPosition,
  forceClose,
}: ITooltipPortalProps) => {
  const isControlled = open !== undefined;

  const tooltipChildrenRef = useRef<HTMLDivElement>(null);
  const portalContentRef = useRef<HTMLDivElement>(null);
  const tooltipContainerRef = useRef<HTMLDivElement>(null);
  const tailRef = useRef<HTMLSpanElement>(null);

  const displayTimeout = useRef<NodeJS.Timeout>(null);
  const delayTimeout = useRef<NodeJS.Timeout>(null);

  const [display, setDisplay] = useState('none');
  const [show, setShow] = useState(false);
  const [blockPointerEvents, setBlockPointerEvents] = useState(false);
  const [tailPositionSet, setTailPositionSet] = useState(false);
  const [anchorPositionSet, setAnchorPositionSet] = useState(false);
  const [disabled, setDisabled] = useState(propsDisabled);

  useClickAwayListener({
    ref: portalContentRef,
    open: !!((onClickAway || onEscape || closeOnEscape) && show && display !== 'none'),
    onPointerDownOutside: (e) => {
      // detect if clicked the tooltipChildrenRef
      const target = e.target as HTMLElement;
      const isTooltipChildren = !!tooltipChildrenRef.current?.contains(target);
      onClickAway?.({ clickedTriggerElement: isTooltipChildren });
    },
    onEscape: () => {
      onEscape?.();
      if (closeOnEscape) {
        handleHide();
      }
    },
    onTab: onTab,
  });

  useEffect(() => {
    // block pointer events until we have a chance to set the position
    // otherwise the tooltip will disappear if the content is hovered while it is still being positioned
    const isShowing = show && display === 'block';
    const positionsSet = tailPositionSet && anchorPositionSet;
    if (!keepOpenOnContentHover && isShowing && positionsSet) {
      setBlockPointerEvents(true);
    }
  }, [anchorPositionSet, display, keepOpenOnContentHover, show, tailPositionSet]);

  const setPositions = useCallback(() => {
    if (!tooltipChildrenRef.current || !portalContentRef.current || !tailRef.current) return;
    setAnchorPosition({
      tooltipChildrenRef,
      portalContentRef,
      tooltipContainerRef,
      position,
      contentDirection,
      horizontalOffset,
      tailSize,
      onComplete: () => {
        setAnchorPositionSet(true);
      },
    });

    setTailPosition({
      tooltipChildrenRef,
      portalContentRef,
      tailRef,
      position,
      contentDirection,
      horizontalOffset,
      tailSize,
      tailHorizontalPosition,
      onComplete: () => {
        setTailPositionSet(true);
      },
    });
  }, [position, contentDirection, horizontalOffset, tailSize, tailHorizontalPosition]);

  usePositionOnScroll({ setPositions, show, tooltipContainerRef });

  const handleShow = useCallback(async () => {
    if (disabled) return;
    if (displayTimeout.current) {
      clearTimeout(displayTimeout.current);
    }
    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current);
    }
    if (delayStart) {
      delayTimeout.current = setTimeout(() => {
        requestAnimationFrame(setPositions);
        setDisplay('block');
        setShow(true);
      }, delayStart);
      return;
    }
    requestAnimationFrame(setPositions);
    setDisplay('block');
    setShow(true);
  }, [delayStart, disabled, setPositions]);

  const handleHide = useCallback(() => {
    if (disabled) return;
    if (delayTimeout.current) {
      clearTimeout(delayTimeout.current);
    }
    if (displayTimeout.current) {
      clearTimeout(displayTimeout.current);
    }
    setShow(false);
    displayTimeout.current = setTimeout(() => {
      setDisplay('none');
    }, 200);
  }, [disabled]);

  useEffect(() => {
    if (forceClose) {
      handleHide();
      setDisabled(true);
    } else {
      setDisabled(propsDisabled);
    }
  }, [forceClose, handleHide, propsDisabled]);

  useEffect(() => {
    if (open !== undefined && !disabled) {
      if (open) {
        handleShow();
      } else {
        handleHide();
      }
    }
  }, [disabled, handleHide, handleShow, open]);

  const handlePortalEnter = useCallback(() => {
    portalHover?.(true);
    if (disabled || isControlled || !showOnHover) return;

    if (!keepOpenOnContentHover && !blockPointerEvents) {
      return handleHide();
    }
  }, [blockPointerEvents, disabled, handleHide, isControlled, keepOpenOnContentHover, portalHover, showOnHover]);

  const handlePortalLeave = useCallback(() => {
    portalHover?.(false);
    if (disabled || isControlled || !showOnHover) return;
  }, [disabled, isControlled, portalHover, showOnHover]);

  // because this component finds fixed positions, when the inner content changes, the position jumps
  // for example, if something takes a second to load within the tooltip, it will re-render, change the size
  // of the tooltip and cause the tooltip position to be off
  // so we need to correct the position when the tooltip height/width changes
  // Note: this resize observer does not trigger on browser resize. So it doesn't overfire
  useEffect(() => {
    const portalEl = portalContentRef.current;
    if (!portalEl || !show || display === 'none') return;

    // track element position
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(() => {
        setPositions();
      });
    });
    if (portalEl) {
      resizeObserver.observe(portalEl);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [portalContentRef, show, display, setPositions]);

  const displayHoverFill = useCallback(() => {
    const isTopOrBottom = position === TooltipPortalPosition.TOP || position === TooltipPortalPosition.BOTTOM;
    return keepOpenOnContentHover && show && display !== 'none' && isTopOrBottom;
  }, [display, keepOpenOnContentHover, position, show]);

  return (
    <div
      className={`tooltip-portal-wrapper ${show ? 'active' : ''} ${tooltipWrapperClass ?? ''}`}
      ref={tooltipContainerRef}
      onPointerEnter={isControlled || !showOnHover ? undefined : handleShow}
      onPointerLeave={isControlled || (!showOnHover && !alwaysCloseOnMouseOut) ? undefined : handleHide}
      onFocus={showOnFocus ? handleShow : undefined}
      onBlur={showOnFocus ? handleHide : undefined}
      role="tooltip"
      style={portalWrapperStyles}
    >
      <div
        ref={tooltipChildrenRef}
        className={`tooltip-portal-children ${childrenWrapperClass} ${show ? 'active' : ''} ${applyActiveSvgFillColor ? 'fill-active-color-svg' : ''}`}
      >
        {/* covers small gap between trigger element and tooltip which prevents hover */}
        <OptionalComponent display={displayHoverFill()}>
          <span
            className="tooltip-portal-hover-fill"
            style={{
              ...(position === TooltipPortalPosition.TOP ? { bottom: '1em' } : {}),
              ...(position === TooltipPortalPosition.BOTTOM ? { top: '1em' } : {}),
              height: `calc(100% + ${`${tailSize + 1 + Math.abs(verticalOffset ?? 0)}px`})`,
            }}
          />
        </OptionalComponent>
        {typeof children === 'function' ? children({ open: show, handleShow, handleHide }) : children}
      </div>
      {/* Portal allows us to overflow any content on the page. It is set to position fixed using css */}
      <Portal className="tooltip-portal">
        <div
          onClick={
            stopPropagation
              ? (e) => {
                  e.stopPropagation();
                  onTooltipClick?.();
                }
              : onTooltipClick
          }
          className={classNames('tooltip-portal-content', contentWrapperClass, animationType, {
            active: show,
          })}
          style={
            {
              '--tooltip-portal-z-index': styles.zIndex,
              '--tooltip-portal-width': styles.width,
              '--tooltip-portal-background-color': styles.backgroundColor,
              '--tooltip-portal-padding': styles.padding,
              '--tooltip-portal-border-radius': styles.borderRadius,
              '--tooltip-portal-color': styles.color,
              '--tooltip-portal-box-shadow': styles.boxShadow,
              '--tooltip-portal-font-size': styles.fontSize,
              '--tooltip-portal-tail-size': `${tailSize}px`,
              '--tooltip-portal-display': display,
              '--tooltip-portal-vertical-offset': verticalOffset ? verticalOffset + 'px' : 0,
              '--tooltip-portal-horizontal-offset': horizontalOffset ? horizontalOffset + 'px' : 0,
              '--tooltip-portal-slide-displacement':
                typeof slideDisplacement === 'number' ? `${slideDisplacement}px` : slideDisplacement,
              ...(blockPointerEvents ? { pointerEvents: 'none' } : {}),
            } as CSSProperties
          }
          ref={portalContentRef}
          onPointerEnter={handlePortalEnter}
          onPointerLeave={handlePortalLeave}
        >
          {tooltipContent}
          <span ref={tailRef} className="tooltip-portal-tail" />
        </div>
      </Portal>
    </div>
  );
};

export default TooltipPortal;
